import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { StaticTaskNavBar } from '@euler/app/flows/task/components/navbar/StaticTaskNavBar';
import { useTaskHeaderMetrics } from '@euler/app/flows/task/functions/useTaskHeaderMetrics';
import { QuotationCatalogTabBar } from '@euler/app/flows/task/pages/quotation/components/QuotationCatalogTabBar';
import { useQuotationContext } from '@euler/app/flows/task/pages/quotation/components/QuotationContext';
import { QuotationSelectionProvider } from '@euler/app/flows/task/pages/quotation/components/QuotationSelectionContext';
import {
  QuotationSubjectsBottomSheet,
  QuotationSubjectsBottomSheetAction,
} from '@euler/app/flows/task/pages/quotation/components/QuotationSubjectsBottomSheet';
import { useQuotationNavigation } from '@euler/app/flows/task/pages/quotation/functions/useQuotationNavigation';
import { useQuotationSubjectSelection } from '@euler/app/flows/task/pages/quotation/functions/useQuotationSubjectSelection';
import { QuotationCatalogTabPage } from '@euler/app/flows/task/pages/quotation/QuotationCatalogTabPage';
import { Icon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Subject } from 'rxjs';

const Tab = createMaterialTopTabNavigator();

export const QuotationCatalogPage = wrapNavigatorScreen(
  ({ onDone }: { onDone: (selection: string[]) => void }) => {
    const { catalog } = useQuotationContext();

    const {
      selection$,
      addCustomQuotationSubject,
      toggleQuotationSubject,
      removeQuotationSubject,
      clearSelection,
    } = useQuotationSubjectSelection();

    const { navBarHeight } = useTaskHeaderMetrics();
    const navigation = useQuotationNavigation();
    const appNav = useAppNavigation();

    const quotationSubjectsModalAction$ = useCreation(
      () => new Subject<QuotationSubjectsBottomSheetAction>(),
      [],
    );

    const key = catalog.categories.map(x => x.id).join(':');

    const onSearchPress = usePersistFn(() => {
      navigation.navigate('_search', {
        selection$,
        toggleQuotationSubject,
      });
    });

    const onAddPress = useCallback(async () => {
      appNav.navigate('_prompt', {
        title: '添加自定义报价项目',
        placeholder: '请输入报价项目名称',
        cancelButtonText: '取消',
        confirmButtonText: '确认',
        confirmButtonDisabled: true,
        errorPlacement: 'below-input',
        onChange: (value, notify$) => {
          const name = value.trim();
          const exists = selection$.value.includes(name);
          notify$.next({
            type: 'button',
            button: 'confirm',
            disabled: exists || !name.length,
          });
          notify$.next({
            type: 'error',
            error: exists ? '该项目已经存在' : undefined,
          });
        },
        onConfirm: async value => {
          value = value.trim();
          if (!value) {
            return;
          }
          addCustomQuotationSubject(value);
        },
      });
    }, [addCustomQuotationSubject, appNav, selection$.value]);

    return (
      <QuotationSelectionProvider
        selection$={selection$}
        addCustomQuotationSubject={addCustomQuotationSubject}
        clearSelection={clearSelection}
        toggleQuotationSubject={toggleQuotationSubject}
        removeQuotationSubject={removeQuotationSubject}
      >
        <StaticTaskNavBar
          title={'管理报价项目'}
          rightOptions={() => (
            <SearchButton
              onSearchPress={onSearchPress}
              onAddPress={onAddPress}
            />
          )}
        />
        <View
          css={`
            flex: 1;
            padding-top: ${navBarHeight}px;
          `}
        >
          <Tab.Navigator
            tabBar={props => (
              <QuotationCatalogTabBar catalog={catalog} {...props} />
            )}
            key={key}
            screenOptions={{
              lazy: true,
            }}
            style={{ flex: 1 }}
          >
            {catalog.categories.map(category => (
              <Tab.Screen
                key={category.name}
                name={category.name}
                component={QuotationCatalogTabPage as any}
                initialParams={{ categoryId: category.id }}
              />
            ))}
          </Tab.Navigator>
        </View>
        <QuotationSubjectsBottomSheet
          action$={quotationSubjectsModalAction$}
          onDone={onDone}
        />
      </QuotationSelectionProvider>
    );
  },
  {
    headerShown: false,
  },
);

const SearchButton = memo(
  ({
    onSearchPress,
    onAddPress,
  }: {
    onSearchPress?: () => void;
    onAddPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <HStack>
        <TouchableOpacity
          hitSlop={PredefinedHitSlops.large}
          onPress={onSearchPress}
          css={`
            width: ${rpx(26)}px;
            height: ${rpx(32)}px;
            align-items: center;
            justify-content: center;
          `}
        >
          <Icon
            name="search"
            size={rpx(20)}
            color="rgba(255, 255, 255, 0.9)"
            css={`
              top: ${rpx(1.5)}px;
            `}
          />
        </TouchableOpacity>
        <TouchableOpacity
          hitSlop={PredefinedHitSlops.large}
          onPress={onAddPress}
          css={`
            height: ${rpx(32)}px;
            margin-left: ${rpx(8)}px;
            flex-direction: row;
            align-items: center;
            justify-content: center;
          `}
        >
          <Icon
            name="plus-circle"
            size={rpx(24)}
            color="rgba(255, 255, 255, 0.9)"
          />
        </TouchableOpacity>
      </HStack>
    );
  },
);
